import { useEffect, useRef, useState } from "react";

/** How long something just done stays in its list with an Undo link before it drops off. */
export const UNDO_MS = 5000;

/**
 * Keeps items that were just done (a task checked off) in their list for a few seconds, where
 * they were, so there's time to undo. The change itself is saved right away; this only delays
 * the item leaving the list.
 */
export function useUndoWindow<T extends { id: string }>(ms = UNDO_MS) {
  const [held, setHeld] = useState<Map<string, { item: T; index: number }>>(new Map());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const release = (id: string) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setHeld((h) => {
      const n = new Map(h);
      n.delete(id);
      return n;
    });
  };
  const hold = (item: T, index: number) => {
    clearTimeout(timers.current.get(item.id));
    timers.current.set(
      item.id,
      setTimeout(() => release(item.id), ms),
    );
    setHeld((h) => new Map(h).set(item.id, { item, index }));
  };
  /** The list as shown: held items in place of (or back where they were, if gone from) the fresh list. */
  const merge = (list: T[]): T[] => {
    const out = list.map((x) => held.get(x.id)?.item ?? x);
    for (const { item, index } of [...held.values()].sort((a, b) => a.index - b.index)) {
      if (!out.some((x) => x.id === item.id)) out.splice(Math.min(index, out.length), 0, item);
    }
    return out;
  };
  return { hold, release, merge, isHeld: (id: string) => held.has(id) };
}
